import { useCallback, useEffect, useRef, useState } from 'react';
import {
  adminDeleteComment,
  ApiError,
  editComment,
  fetchComments,
  fetchVersion,
  postComment,
  toggleReaction,
  type UploadedMedia,
} from '../api/client';

const POLL_INTERVAL_MS = 4000;
import { CommentForm } from '../components/CommentForm';
import { CommentsList } from '../components/CommentsList';
import { ru } from '../i18n/ru';
import type { CommentItem, CommentsList as CommentsListData, PostContext } from '../types';

interface Props {
  context: PostContext;
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return ru.banned;
    if (err.status === 400) return ru.blocked;
  }
  return err instanceof Error ? err.message : ru.errorGeneric;
}

export function CommentsPage({ context }: Props) {
  const [data, setData] = useState<CommentsListData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banned, setBanned] = useState(false);
  const [replyTo, setReplyTo] = useState<CommentItem | null>(null);
  const [editing, setEditing] = useState<CommentItem | null>(null);
  const [asChannel, setAsChannel] = useState(false);
  const lastVersion = useRef<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await fetchComments(context);
      setData(next);
    } catch (e: unknown) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [context]);

  // Тихое фоновое обновление (без экрана загрузки) — для real-time-поллинга.
  const refresh = useCallback(async () => {
    try {
      setData(await fetchComments(context));
    } catch {
      /* поллинг не шумит ошибками — следующий тик попробует снова */
    }
  }, [context]);

  useEffect(() => {
    void load();
  }, [load]);

  // Поллинг версии поста: рефетч ленты только когда на сервере что-то изменилось.
  useEffect(() => {
    const timer = setInterval(async () => {
      if (document.hidden) return;
      try {
        const v = await fetchVersion(context);
        if (lastVersion.current === null) {
          lastVersion.current = v;
        } else if (v !== lastVersion.current) {
          lastVersion.current = v;
          void refresh();
        }
      } catch {
        /* транзиентная ошибка поллинга — игнорируем */
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [context, refresh]);

  const handleSubmit = useCallback(
    async (text: string, media?: UploadedMedia) => {
      if (editing) {
        try {
          const updated = await editComment(editing.id, { text, media });
          setData((prev) =>
            prev
              ? { ...prev, items: prev.items.map((c) => (c.id === updated.id ? updated : c)) }
              : prev,
          );
          setEditing(null);
        } catch (e: unknown) {
          // Окно правки истекло или комментарий удалили — выходим из режима
          // правки и подтягиваем актуальную ленту.
          if (e instanceof ApiError && (e.status === 403 || e.status === 404)) {
            setEditing(null);
            void refresh();
          }
          throw new Error(e instanceof ApiError ? e.message : ru.editError);
        }
        return;
      }
      try {
        const created = await postComment(context, {
          text,
          media,
          replyToCommentId: replyTo?.id,
          asChannel,
        });
        setData((prev) =>
          prev ? { ...prev, items: [...prev.items, created], total: prev.total + 1 } : prev,
        );
        setReplyTo(null);
      } catch (e: unknown) {
        if (e instanceof ApiError && e.status === 403) setBanned(true);
        throw new Error(errorMessage(e));
      }
    },
    [context, replyTo, asChannel, editing, refresh],
  );

  const handleEdit = useCallback((comment: CommentItem) => {
    setReplyTo(null);
    setEditing(comment);
  }, []);

  const handleToggleReaction = useCallback(async (comment: CommentItem, emoji: string) => {
    try {
      const reactions = await toggleReaction(comment.id, emoji);
      setData((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((c) => (c.id === comment.id ? { ...c, reactions } : c)),
            }
          : prev,
      );
    } catch (e: unknown) {
      setError(errorMessage(e));
    }
  }, []);

  const handleDelete = useCallback(async (comment: CommentItem) => {
    if (!window.confirm(ru.deleteConfirm)) return;
    try {
      const updated = await adminDeleteComment(comment.id);
      setData((prev) =>
        prev
          ? { ...prev, items: prev.items.map((c) => (c.id === comment.id ? updated : c)) }
          : prev,
      );
    } catch (e: unknown) {
      setError(errorMessage(e) || ru.deleteError);
    }
  }, []);

  if (loading && !data) return <div className="loading">{ru.loading}</div>;
  if (error && !data) return <div className="error">{error}</div>;
  if (!data) return null;

  return (
    <div className="page">
      <header className="page-header">
        <span className="role-label">{ru.role[data.role]}</span>
        {data.role === 'admin' && data.unread_alerts > 0 && (
          <span className="admin-badge">{ru.adminBadge(data.unread_alerts)}</span>
        )}
      </header>

      <CommentsList
        items={data.items}
        role={data.role}
        viewerUserId={data.viewer_user_id}
        editWindowSeconds={data.edit_window_seconds}
        onDelete={handleDelete}
        onEdit={handleEdit}
        onReply={setReplyTo}
        onToggleReaction={handleToggleReaction}
      />

      <CommentForm
        onSubmit={handleSubmit}
        disabled={banned}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        editing={editing}
        onCancelEdit={() => setEditing(null)}
        isAdmin={data.role === 'admin'}
        channelName={data.channel_name}
        asChannel={asChannel}
        onSetAsChannel={setAsChannel}
      />

      {error && <div className="error">{error}</div>}
    </div>
  );
}
